import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import './LandingPage.css';
import './StoryPage.css';
import AuthModal from './components/auth/AuthModal';
import AvatarMenu from './components/shared/AvatarMenu';
import SiteFooter from './components/shared/SiteFooter';
import { signOutUser } from './utils/auth';
import jessicaHeadshot from './imges/jessica-headshot.png';

/* ─── SVG icons — same recipe as LandingPage.jsx ─── */
const IconTranslate = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);
const IconUnlock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);
const IconClock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconShieldCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const IconPeople = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconGlobe = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconTarget = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

/* ─── Static content ─── */
const DELIVERS = [
  { icon: <IconTranslate />, title: 'Strategic Translation', desc: 'We translate your experience into ATS-ready language that gets you noticed.' },
  { icon: <IconUnlock />, title: 'Beyond Auto-Rejections', desc: 'We help position your story so it can move past filters and connect with real decision-makers.' },
  { icon: <IconClock />, title: 'Time Saved', desc: 'What once took hours, days, or months of frustration is now done with clarity and speed.' },
  { icon: <IconShieldCheck />, title: 'Authentic & Compliant', desc: 'We do not fabricate. We do not exaggerate. We elevate the truth.' },
];

const WHO_FOR = [
  { icon: <IconPeople />, title: 'For Every Professional', desc: 'New graduates, career changers, executives, and experienced pros.' },
  { icon: <IconGlobe />, title: 'Industry Agnostic', desc: 'Built for every industry, every role, every level.' },
  { icon: <IconLock />, title: 'Private & Secure', desc: 'Your information. Your privacy. Our priority.' },
  { icon: <IconTarget />, title: 'Built With Purpose', desc: 'Real experience. Real research. Real results.' },
];

const StoryPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const session = useSelector((state) => state.auth.session);
  const user = useSelector((state) => state.auth.user);
  const credits = useSelector((state) => state.auth.credits);
  const [showModal, setShowModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const firstMenuLinkRef = useRef(null);

  const appDest = credits === 0 ? '/get-credits' : '/create';

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (menuOpen) {
      firstMenuLinkRef.current?.focus();
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 720) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignOut = () => signOutUser({ dispatch, navigate, userId: user?.id });

  useEffect(() => {
    if (session && showModal) {
      setShowModal(false);
      if (!sessionStorage.getItem('pendingSku')) {
        navigate(appDest);
      }
    }
  }, [session, showModal, navigate, appDest]);

  const handleCta = () => {
    if (session) {
      navigate(appDest);
    } else {
      setShowModal(true);
    }
  };

  return (
    <div className="lp-root story-root">
      {showModal && <AuthModal onClose={() => setShowModal(false)} />}
      <div className="lp-ambient" aria-hidden="true" />

      {/* ── NAV (persistent — same as the rest of the site) ── */}
      <nav className="lp-nav">
        <Link to="/" className="lp-nav-brand" style={{ textDecoration: 'none' }}>
          <span className="lp-nav-logomark">Z</span>
          <span className="lp-nav-logo">ZYNACULAR</span>
        </Link>
        <div className="lp-nav-links">
          <a href="/#how-it-works" className="lp-nav-link">How it works</a>
          <a href="/#what-you-get" className="lp-nav-link">Features</a>
          <a href="/#pricing" className="lp-nav-link">Pricing</a>
          <Link to="/story" className="lp-nav-link">Our Story</Link>
          <Link to="/support" className="lp-nav-link">Support</Link>
          <span className="lp-nav-sep" aria-hidden="true" />
          {session ? (
            <>
              <AvatarMenu
                userEmail={user?.email}
                credits={credits}
                onSignOut={handleSignOut}
              />
              <button onClick={handleCta} className="lp-nav-cta">Go to App →</button>
            </>
          ) : (
            <>
              <button onClick={handleCta} className="lp-nav-signin">Sign in</button>
              <button onClick={handleCta} className="lp-nav-cta">Launch App →</button>
            </>
          )}
          <button
            type="button"
            ref={hamburgerRef}
            className="lp-nav-hamburger"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="lp-mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="lp-hamburger-bar" />
            <span className="lp-hamburger-bar" />
            <span className="lp-hamburger-bar" />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <>
          <div className="lp-mobile-menu-overlay" onClick={closeMenu} aria-hidden="true" />
          <nav id="lp-mobile-menu" className="lp-mobile-menu" aria-label="Mobile">
            <a
              ref={firstMenuLinkRef}
              href="/#how-it-works"
              className="lp-mobile-menu-link"
              onClick={closeMenu}
            >
              How it works
            </a>
            <a href="/#what-you-get" className="lp-mobile-menu-link" onClick={closeMenu}>Features</a>
            <a href="/#pricing" className="lp-mobile-menu-link" onClick={closeMenu}>Pricing</a>
            <Link to="/story" className="lp-mobile-menu-link" onClick={closeMenu}>Our Story</Link>
            <Link to="/support" className="lp-mobile-menu-link" onClick={closeMenu}>Support</Link>
          </nav>
        </>
      )}

      {/* ── HERO ── */}
      <section className="lp-hero story-hero">
        <span className="lp-hero-eyebrow">THE STORY BEHIND</span>
        <h1 className="lp-hero-headline">ZYNACULAR</h1>
        <p className="lp-hero-sub">Built from experience. Created for opportunity.</p>
      </section>

      {/* ── FOUNDER STORY ── */}
      <section className="lp-wide-section story-founder">
        <div className="story-founder-narrative">
          <p className="story-lede">
            <span className="story-dropcap">Z</span>ynacular was created from sheer frustration, and a
            mission to help people be seen. After 25 years in the communications industry and rising to
            the role of Vice President, I experienced a reduction in force that changed my path. I was
            fortunate enough to return and help create a division that still stands today. But when it
            came time to search for my next opportunity, I found myself facing a hiring system I did not
            fully understand.
          </p>
          <blockquote className="story-pullquote">
            I had the experience. I had the leadership. I had the accomplishments. But I was not getting through.
          </blockquote>
          <p>
            That is when I discovered how Applicant Tracking Systems (ATS), auto-rejections, keyword gaps,
            outdated formatting, and misunderstood experience can quietly keep highly qualified people from
            ever reaching a real decision-maker.
          </p>
          <p>
            What took hours a day, and sometimes months of frustration, became the foundation for something
            bigger. I realized most people do not have the time, tools, or knowledge to rebuild every resume,
            cover letter, LinkedIn profile, bio, and career document for every opportunity they pursue.
          </p>
          <p>
            That is why I created Zynacular. Zynacular takes a person's real skills, real experience, real
            accomplishments, and real story, translating them into language that both technology and
            employers can understand.
          </p>
        </div>
        <div className="story-founder-side">
          <img
            src={jessicaHeadshot}
            alt="Jessica Mruk-Richards, Founder and CEO of Zynacular"
            className="story-portrait"
          />
          <div className="story-quote-card">
            <p>
              "I created Zynacular because I know what it feels like to have the experience and still not
              be seen. This platform was built to change that."
            </p>
          </div>
          <div className="story-signature">
            <div className="story-signature-name">Jessica Mruk-Richards</div>
            <div className="story-signature-title">Founder &amp; CEO</div>
          </div>
        </div>
      </section>

      {/* ── VISION ── */}
      <section className="lp-how-section story-vision">
        <p className="lp-section-label">OUR VISION</p>
        <h2 className="lp-section-heading">
          A world where qualified people are no longer filtered out before they are seen.
        </h2>
        <p className="lp-section-sub">Because your story matters. And your future is worth fighting for.</p>
      </section>

      {/* ── WHAT ZYNACULAR DELIVERS ── */}
      <section className="lp-wide-section">
        <p className="lp-section-label">WHAT ZYNACULAR DELIVERS</p>
        <div className="story-deliver-list">
          {DELIVERS.map((item) => (
            <div key={item.title} className="story-deliver-row">
              <div className="lp-feature-icon">{item.icon}</div>
              <div>
                <div className="lp-feature-title">{item.title}</div>
                <p className="lp-feature-desc">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── OUR PROMISE ── */}
      <section className="lp-how-section story-promise">
        <p className="lp-section-label">OUR PROMISE</p>
        <p className="story-promise-lead">We do not fabricate. We do not exaggerate.</p>
        <p className="story-promise-support">
          We simply help people present what they have already earned in a way the modern hiring world can recognize.
        </p>
      </section>

      {/* ── PERSONAL NOTE ── */}
      <section className="lp-how-section story-note">
        <h2 className="lp-final-cta-heading">This is more than software to me.</h2>
        <p className="lp-final-cta-sub story-note-sub">
          It is personal. It is purpose-driven. It is a passion project built for every person who has
          ever felt invisible in the job search.
        </p>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section className="lp-wide-section">
        <p className="lp-section-label">WHO IT'S FOR</p>
        <div className="story-feature-grid">
          {WHO_FOR.map((item) => (
            <div key={item.title} className="lp-feature-card">
              <div className="lp-feature-icon">{item.icon}</div>
              <div className="lp-feature-title">{item.title}</div>
              <p className="lp-feature-desc">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CLOSING CTA ── */}
      <section className="lp-final-cta">
        <div className="lp-final-cta-glow" aria-hidden="true" />
        <h2 className="lp-final-cta-heading">Zynacular is the language of opportunity.</h2>
        <p className="lp-final-cta-sub">One story. One platform. Endless opportunities.</p>
        <button onClick={handleCta} className="lp-cta">
          Create My Documents →
        </button>
      </section>

      <SiteFooter />
    </div>
  );
};

export default StoryPage;
